import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "../layout";
import Header from "../header/Header";
import { api } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";

const ytThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

function Tutorials() {
  useMyContext();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState("");
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/tutorials");
        setVideos(data || []);
        setActiveId((data?.[0]?.videoId) || "");
      } catch (e) {
        setError(e?.response?.data?.error || "Failed to load tutorials");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeVideo = useMemo(() => videos.find(v => v.videoId === activeId), [videos, activeId]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter(v =>
      (v.title || "").toLowerCase().includes(q) ||
      (v.description || "").toLowerCase().includes(q) ||
      (v.url || "").toLowerCase().includes(q)
    );
  }, [videos, query]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length]);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  // Keep page in range if filter reduces dataset
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  return (
    <DashboardLayout>
      <Header title="Tutorials" />
      <div className="p-4 md:p-6 bg-cus-gray min-h-[80vh]">
        <div className="max-w-[1120px] mx-auto">
        {/* Search bar */}
        <div className="mb-4">
          <div className="bg-white rounded-full px-5 h-12 flex items-center gap-3 border border-gray-200 shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/30">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 21L16.65 16.65" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <input
              ref={searchRef}
              type="text"
              className="flex-1 outline-none text-base bg-transparent placeholder-gray-400"
              placeholder="Search tutorials"
              value={query}
              onChange={(e)=>{ setQuery(e.target.value); setCurrentPage(1); }}
              onKeyDown={(e)=>{ if(e.key==='Enter'){ const first = filtered[0]; if(first){ setActiveId(first.videoId); setShowPlayer(true);} } }}
            />
          </div>
        </div>
        {loading ? (
          <div className="bg-white rounded-md p-6 text-sm">Loading tutorials…</div>
        ) : error ? (
          <div className="bg-white rounded-md p-6 text-sm text-red-600">{error}</div>
        ) : (
          <div className="space-y-4">
            {/* Results summary */}
            {query.trim().length > 0 && (
              <div className="text-xs text-gray-500 -mt-2">{filtered.length} result{filtered.length!==1 && 's'} for “{query}” • <button onClick={()=>{setQuery(''); setCurrentPage(1);}} className="text-blue-600 hover:underline">Clear</button></div>
            )}
            {filtered.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center text-sm text-gray-600 border border-gray-200">
                <p className="font-medium text-gray-700">No tutorials found</p>
                <p className="mt-1">Try a different keyword or <button onClick={()=>{setQuery(''); setCurrentPage(1);}} className="text-blue-600 hover:underline">clear search</button>.</p>
              </div>
            )}
            {paginated.map((v) => (
              <button
                key={v._id}
                onClick={() => { setActiveId(v.videoId); setShowPlayer(true); }}
                aria-label={`Play ${v.title || 'tutorial'}`}
                className={`group w-full flex gap-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md overflow-hidden text-left transition`}
              >
                {/* Thumbnail with rounded border like YouTube */}
                <div className="relative rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 w-[360px] h-[202px] md:w-[480px] md:h-[270px]">
                  <img
                    src={ytThumb(v.videoId)}
                    alt={v.title || 'thumbnail'}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                  {/* Play overlay */}
                  <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/10 transition-colors">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-gray-800 rounded-full h-10 w-10 grid place-items-center shadow-md">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                {/* Right-side meta */}
                <div className="py-2 pr-4 flex-1 min-w-0">
                  <p className="text-[18px] font-semibold leading-snug line-clamp-2 text-gray-900 group-hover:underline">{v.title || 'Tutorial'}</p>
                  <div className="text-[12px] text-gray-500 mt-1">One Union Solutions • Tutorials</div>
                  {v.description && (
                    <p className="text-[13px] text-gray-600 mt-2 line-clamp-2">
                      {v.description}
                    </p>
                  )}
                </div>
              </button>
            ))}

            {/* Pagination */}
            {totalPages >= 1 && (
              <div className="flex items-center justify-center py-3 gap-3 select-none">
                {/* Left chevron */}
                <button
                  aria-label="Previous page"
                  disabled={currentPage===1}
                  onClick={()=>setCurrentPage(p=>Math.max(1,p-1))}
                  className={`h-8 w-8 grid place-items-center rounded-full border ${currentPage===1? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200':'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'}`}
                >
                  <HiOutlineChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-2">
                  {Array.from({length: totalPages}).map((_,i)=>{
                    const page=i+1;
                    const shouldShow = totalPages<=7 || Math.abs(page-currentPage)<=1 || page===1 || page===totalPages;
                    if(!shouldShow) return (i===1 || i===totalPages-2) ? <span key={i} className="text-gray-400 px-1">…</span> : null;
                    const isActive = page===currentPage;
                    return (
                      <button
                        key={page}
                        onClick={()=>setCurrentPage(page)}
                        className={isActive
                          ? 'px-3 py-1 rounded-lg border border-blue-500 text-blue-600 font-medium shadow-[0_0_0_3px_rgba(59,130,246,0.15)]'
                          : 'px-2.5 py-1 rounded-lg text-gray-700 hover:text-blue-600 border border-transparent hover:border-blue-300'}
                      >{page}</button>
                    );
                  })}
                </div>

                {/* Right chevron */}
                <button
                  aria-label="Next page"
                  disabled={currentPage===totalPages}
                  onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))}
                  className={`h-8 w-8 grid place-items-center rounded-full border ${currentPage===totalPages? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200':'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'}`}
                >
                  <HiOutlineChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
      {showPlayer && activeId && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4" onClick={()=>setShowPlayer(false)}>
          <div className="bg-black rounded-lg w-full max-w-4xl aspect-video" onClick={(e)=>e.stopPropagation()}>
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${activeId}`}
              title={activeVideo?.title || 'Tutorial video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Tutorials;
